import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Spinner } from "react-bootstrap";
import { Code2, Eye, CheckCircle2, FlaskConical, Download } from "lucide-react";
import apiClient from "../../utils/apiClient";
import { SearchInput, AppButton, AppModal, EmptyState, Paginator, PageHeader } from "../ui";
import CommonToaster from "../Common/CommonToaster";
import { noCopyProps } from "./marketplaceShared";
import "./Marketplace.scss";

const PAGE_SIZE = 15;
const DIFFICULTIES = ["", "Easy", "Medium", "Hard"];

/**
 * MarketplaceCodingPage — browse and INSTALL platform coding questions.
 * Published library questions live in the marketplace only; an institute must
 * install (clone) the ones it wants into its own Coding Question Bank before
 * they are usable. Multi-select + Install, or install a single question from
 * the preview. Preview is view-only (copy/paste blocked).
 */
const MarketplaceCodingPage = () => {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [difficulty, setDifficulty] = useState("");
  const [sel, setSel] = useState(() => new Set());
  const [installing, setInstalling] = useState(false);
  const [preview, setPreview] = useState(null);     // full question doc
  const [previewLoading, setPreviewLoading] = useState(false);
  const [toast, setToast] = useState({ show: false, message: "", type: "success" });

  const fetchRows = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await apiClient.get("/questions/list", {
        params: {
          scope: "library",
          page,
          limit: PAGE_SIZE,
          sort: "newest",
          ...(search && { search }),
          ...(difficulty && { difficulty }),
        },
      });
      setRows(data.questions || []);
      setTotal(data.total || 0);
      setTotalPages(data.totalPages || 0);
    } catch { /* empty state */ }
    setLoading(false);
  }, [page, search, difficulty]);

  useEffect(() => { fetchRows(); }, [fetchRows]);
  useEffect(() => { setPage(1); }, [search, difficulty]);
  // Selection is page-scoped — reset when the visible set changes.
  useEffect(() => { setSel(new Set()); }, [page, search, difficulty]);

  const toggleSel = (id) => {
    setSel((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const install = async (ids, closePreviewAfter = false) => {
    if (!ids.length) return;
    setInstalling(true);
    try {
      const { data } = await apiClient.post("/questions/install-marketplace", { ids });
      setToast({ show: true, message: data.message || "Questions installed", type: "success" });
      setSel(new Set());
      if (closePreviewAfter) setPreview(null);
      await fetchRows();
    } catch (err) {
      setToast({ show: true, message: err.response?.data?.message || "Install failed", type: "error" });
    }
    setInstalling(false);
  };

  const openPreview = async (q) => {
    setPreviewLoading(true);
    setPreview({ ...q, _loading: true });
    try {
      const { data } = await apiClient.get(`/questions/${q._id}`);
      setPreview({ ...data.question, alreadyImported: q.alreadyImported });
    } catch {
      setPreview(q); // fall back to the list row's data
    }
    setPreviewLoading(false);
  };

  const previewLangs = useMemo(
    () => (preview?.categories || []).map((c) => (typeof c === "object" ? c.name : c)),
    [preview],
  );
  const visibleTests = (preview?.testCases || []).filter((t) => !t.isHidden);
  const hiddenCount = (preview?.testCases || []).length - visibleTests.length;

  // Only not-yet-installed selected rows are actually installable.
  const installableSel = useMemo(
    () => [...sel].filter((id) => !rows.find((r) => r._id === id)?.alreadyImported),
    [sel, rows],
  );

  return (
    <div className="mkp-root">
      {/* Title + subheading render in the TopNav via the PageHeader portal. */}
      <PageHeader
        icon={Code2}
        backButton
        onBack={() => navigate("/marketplace")}
        title="Coding Questions"
        description="Real-world problems — install the ones you want into your Coding Question Bank"
      />

      <div className="mkp-toolbar">
        <SearchInput value={search} onChange={setSearch} placeholder="Search coding questions..." />
        <select className="mkp-filter-select" style={{ width: "auto" }} value={difficulty} onChange={(e) => setDifficulty(e.target.value)}>
          {DIFFICULTIES.map((d, i) => (
            <option key={d || i} value={d}>{d || "All Difficulties"}</option>
          ))}
        </select>
        {/* Bulk install actions share the toolbar row when a selection exists. */}
        {sel.size > 0 && (
          <div className="mkp-toolbar-bulk">
            <span className="qdrive-bulkbar-count">{sel.size} selected</span>
            <AppButton variant="default" size="sm" disabled={installing || installableSel.length === 0} onClick={() => install(installableSel)}>
              <Download size={14} /> <span>{installing ? "Installing…" : `Install ${installableSel.length || ""}`.trim()}</span>
            </AppButton>
            <AppButton variant="ghost" size="sm" onClick={() => setSel(new Set())}>Clear</AppButton>
          </div>
        )}
      </div>

      {loading ? (
        <div className="mkp-loading"><Spinner animation="border" size="sm" /></div>
      ) : rows.length === 0 ? (
        <EmptyState
          icon={<Code2 size={40} />}
          title={search || difficulty ? "No matches" : "Nothing published yet"}
          description={
            search || difficulty
              ? "Try a different search or difficulty."
              : "No coding questions have been published to the marketplace yet."
          }
        />
      ) : (
        <>
          <div className="mkp-rows">
            {rows.map((q) => (
              <div
                className="mkp-row"
                key={q._id}
                role="button"
                tabIndex={0}
                onClick={() => openPreview(q)}
                onKeyDown={(e) => { if (e.key === "Enter") openPreview(q); }}
              >
                <label className="mkp-row-check" onClick={(e) => e.stopPropagation()}>
                  <input
                    type="checkbox"
                    checked={sel.has(q._id)}
                    disabled={q.alreadyImported}
                    onChange={() => toggleSel(q._id)}
                    aria-label={`Select ${q.title}`}
                  />
                </label>
                <span className="mkp-row-icon mkp-row-icon--green"><Code2 size={20} /></span>
                <div className="mkp-row-main">
                  <div className="mkp-row-title">
                    {q.title}
                    <span className={`mkp-chip mkp-chip--${(q.difficulty || "").toLowerCase()}`}>{q.difficulty}</span>
                  </div>
                  <div className="mkp-row-sub">
                    {[
                      (q.categories || []).map((c) => (typeof c === "object" ? c.name : c)).slice(0, 4).join(", "),
                      (q.labels || []).slice(0, 3).join(" · "),
                      `${(q.testCases || []).length} test cases`,
                    ].filter(Boolean).join(" — ")}
                  </div>
                </div>
                <div className="mkp-row-side">
                  <AppButton variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); openPreview(q); }}>
                    <Eye size={13} /> Preview
                  </AppButton>
                  {q.alreadyImported ? (
                    <span className="mkp-chip mkp-chip--installed"><CheckCircle2 size={11} /> Installed</span>
                  ) : (
                    <AppButton variant="default" size="sm" disabled={installing} onClick={(e) => { e.stopPropagation(); install([q._id]); }}>
                      <Download size={13} /> Install
                    </AppButton>
                  )}
                </div>
              </div>
            ))}
          </div>
          <Paginator page={page} totalPages={totalPages} total={total} pageSize={PAGE_SIZE} onChange={setPage} infoLabel="questions" />
        </>
      )}

      {/* ── Preview modal (view-only, copy blocked) ──
          The icon read `var(--mkp-green, #16a34a)`, and --mkp-green is scoped to
          .mkp-page in Marketplace.scss while AppModal portals OUT of it — so it
          always painted the literal. --success is that green by intent and
          follows the theme. Found 2026-08-25 by lint:tokens --check outofscope. */}
      {preview && (
        <AppModal
          show
          onHide={() => setPreview(null)}
          size="lg"
          icon={<Code2 size={18} style={{ color: "hsl(var(--success))" }} />}
          title={preview.title}
          footer={
            <>
              <AppButton variant="outline" onClick={() => setPreview(null)}>Close</AppButton>
              {preview.alreadyImported ? (
                <AppButton variant="default" onClick={() => navigate("/questions-management")}>
                  Open in Question Bank
                </AppButton>
              ) : (
                <AppButton variant="default" disabled={installing} onClick={() => install([preview._id], true)}>
                  <Download size={15} /> {installing ? "Installing…" : "Install to my bank"}
                </AppButton>
              )}
            </>
          }
        >
          {previewLoading ? (
            <div className="mkp-loading"><Spinner animation="border" size="sm" /></div>
          ) : (
            <div className="mkp-code-preview" {...noCopyProps}>
              <div style={{ display: "flex", gap: "0.3rem", flexWrap: "wrap" }}>
                <span className={`mkp-chip mkp-chip--${(preview.difficulty || "").toLowerCase()}`}>{preview.difficulty}</span>
                {previewLangs.map((l) => (
                  <span key={l} className="mkp-chip mkp-chip--coding">{l}</span>
                ))}
                {(preview.labels || []).slice(0, 5).map((l) => (
                  <span key={l} className="mkp-chip">{l}</span>
                ))}
              </div>

              <div
                className="mkp-code-desc"
                dangerouslySetInnerHTML={{ __html: preview.description || "" }}
              />

              {previewLangs.map((l) =>
                preview.sampleCode?.[l] ? (
                  <React.Fragment key={l}>
                    <div className="mkp-code-label">Starter template · {l}</div>
                    <pre className="mkp-code-block">{preview.sampleCode[l]}</pre>
                  </React.Fragment>
                ) : null,
              )}

              {visibleTests.length > 0 && (
                <>
                  <div className="mkp-code-label"><FlaskConical size={11} /> Sample test cases</div>
                  {visibleTests.slice(0, 3).map((tc, i) => (
                    <pre className="mkp-code-block" key={i}>{`Input:\n${tc.input}\n\nExpected output:\n${tc.expectedOutput}`}</pre>
                  ))}
                </>
              )}
              {hiddenCount > 0 && (
                <div className="mkp-code-note">
                  + {hiddenCount} hidden test case{hiddenCount === 1 ? "" : "s"} used for grading.
                </div>
              )}
              <div className="mkp-code-note">
                {preview.alreadyImported
                  ? "Already in your Coding Question Bank — assign it from there."
                  : "Install this question to add it to your Coding Question Bank, then assign it to students."}
              </div>
            </div>
          )}
        </AppModal>
      )}

      <CommonToaster show={toast.show} message={toast.message} type={toast.type} onClose={() => setToast({ ...toast, show: false })} />
    </div>
  );
};

export default MarketplaceCodingPage;
